import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import Box from '@components/Box';
import DataTable from '@containers/DataTable';
import NoItems from '@components/NoItems';
import ParcelModalWindow from '@containers/ParcelModalWindow';
import Popup from '@components/Popup';
import TableSelectionPanel from '@components/Table/TableSelectionPanel';
import { add, isFuture } from 'date-fns';
import { AddSellerPanel } from '@components/AddSeller';
import { Button, ButtonSize } from '@components/Button';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { formatSumFromObject } from '@utils/formatValues';
import { getReturnsPage, Return, ReturnItem, ReturnTypes } from '@api/apiClient';
import { Modal } from '@components/Modal';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes';
import { Text, TextColor, TextSize } from '@components/Text';
import TrackingNumber from "@components/TrackingNumber";
import CancelBtn from "../components/CancelBtn";
import { useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

const ReturnsTab = ({
  filters,
  pending,
  downloadingBtn,
  handlerSetSavedReturnsSelection,
  savedReturnsSelection,
  refreshData,
  sellersNotFound,
  noData,
}: {
  filters: FilterModel[],
  pending: boolean,
  downloadingBtn: ReactElement,
  handlerSetSavedReturnsSelection: (value: (((prevState: string[]) => string[]) | string[])) => void,
  savedReturnsSelection: string[],
  refreshData: () => void,
  sellersNotFound: boolean,
  noData: boolean,
}) => {
  const [detailId, setDetailId] = useState<string>('');
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState(false);
  const [returnType, setReturnType] = useState('');

  const { t } = useTranslation();

  const closeParcelWindow = () => {
    setIsOpenParcelWindow(false);
  }

  const columns: TableColumn<Return>[] = [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<Return>) => (
        <TrackingNumber
          externalId={row.data?.externalId}
          trackNumber={row.data?.trackNumber}
          onClick={() => {
            setIsOpenParcelWindow(true)
            setDetailId(row.data.id)
            setReturnType(row.data.returnType);
          }}
        />
      ),
      alignment: "left",
      width: "auto"
    },
    {
      header: t("returnTable.attachment"),
      accessor: (row: TableRowType<Return>) => {
        const firstItem = row.data.items?.[0];
        return firstItem ? (
          <a href={firstItem.url} target="_blank" rel="noreferrer">
            {firstItem.name}
          </a>
        ) : null;
      },
      width: "250px"
    },
    {
      header: t("returnTable.quantity"),
      accessor: (row: TableRowType<Return>) => row.data.items.length ? row.data.items.reduce((accumulator: number, item: ReturnItem) => accumulator + item.quantity, 0) : '',
      alignment: "left",
      type: TableColumnType.TERTIARY
    },
    {
      header: t("returnTable.service"),
      accessor: (row: TableRowType<Return>) => row.data.serviceName,
      width: "200px"
    },
    {
      header: t("returnTable.cost"),
      accessor: (row: TableRowType<Return>) => (
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {formatSumFromObject(row.data?.declaredValue)}
          </Text>
      ),
      alignment: "right",
      width: "120px",
    },
    {
      header: t("returnTable.delivery"),
      accessor: (row: TableRowType<Return>) => (
          <div className={styles.price}>
            {row.data.returnType === 'DeliverToSeller' && formatSumFromObject(row.data.deliveryPrice)}
            {row.data.returnType === 'DeliverToRussia' && formatSumFromObject(row.data.russiaDeliveryPrice)}
          </div>
      ),
      type: TableColumnType.PRIMARY,
      width: "120px",
    },
    {
      header: t("returnTable.seller"),
      accessor: (row: TableRowType<Return>) => (
        <AvatarWithMarketplace
          name={row.data.sellerName}
          id={row.data.sellerId}
          marketplace={row.data.marketplaceType}
        />
      ),
      alignment: 'center',
      noOverflow: true
    },
    {
      header: "",
      accessor: (row: TableRowType<Return>) => {
        const endDate = add(row.data.returnAt, { hours: 1 });
        return (
        <Box beforeSpace pr={10}>
          {(row.data.isCancelable && isFuture(new Date(endDate))) ? (
            <CancelBtn
              id={row.data.id}
              endDate={endDate}
              estimatedUtilizationDate={row.data.estimatedUtilizationDate ?? ""}
              tabType="returns"
              handleRefresh={() => {
                handlerSetSavedReturnsSelection([]);
                refreshData();
              }}
            />
          ) : (
            <Popup text={t("disposalTable.resetButtonNoty")}>
              <Button
                disabled
                onClick={() => {}}
                size={ButtonSize.XS}
                isRounded
                title={(
                  <>
                    {t("disposalTable.resetButton")}
                  </>
                )}
              />
            </Popup>
          )}
        </Box>
      )},
      alignment: 'center',
      noOverflow: true,
      width: "auto"
    },
  ];

  if (sellersNotFound) {
    return (
      <AddSellerPanel isFirstSeller />
    );
  }

  if (noData) {
    <NoItems
      title={t("noItems.returns.title")}
      description={t("noItems.returns.description")}
    />
  }

  return (
    <TableSelectionPanel
        isShown={savedReturnsSelection.length > 0}
        items={[
          {
            label: t("awaitingTableSelectionPanel.selected"),
            value: savedReturnsSelection.length,
          }
        ]}
        buttons={[downloadingBtn]}
    >
      <DataTable<Return>
        queryKey="returns-page"
        pending={pending}
        getData={getReturnsPage}
        columns={columns}
        selectable
        selectedIds={savedReturnsSelection}
        onSelectionChange={handlerSetSavedReturnsSelection}
        notFound={t("returnTab.notReturns")}
        filters={[
          ...filters,
          {
            name: "ReturnTypes",
            value: [ReturnTypes.DeliverToRussia, ReturnTypes.DeliverToSeller],
          },
        ]}
      />

      {isOpenParcelWindow ? (
        <Modal
          isOpen={isOpenParcelWindow}
          onClose={closeParcelWindow}
          onClickOverlay={closeParcelWindow}
          width={900}
          bodyWithoutIndentations
        >
          <ParcelModalWindow
              parcelId={detailId}
              returnType={returnType}
          />
        </Modal>
      ) : null}
    </TableSelectionPanel>
  );
};

export default ReturnsTab;
